/**
 * Client error reporting — Sentry único (external webhook removed 2026-09-01)
 * Nurbak 2026 + Sentry Gaps 2026 — observabilidad única.
 * PII filter: never send email, base64, text, extractedText full to Sentry or API.
 * Fail-open: reporting never throws, always returns.
 * Sends to Sentry (captureException) + POST /api/error-report (relay: persiste
 * en admin_events SOLO — nunca re-captura en Sentry, ver Defecto B más abajo).
 * Never external webhook — 2026-09-01 all external webhooks removed.
 *
 * 2026-10-02 Sentry JAVASCRIPT-NEXTJS-1S/1P (regresado, mismo trace_id —
 * UN error del navegador reportado por DOS rutas):
 *
 *   DEFECTO A — el error real era irrecuperable.
 *   `sanitizeValue()` reconstruía CUALQUIER objeto con `Object.entries()`, que
 *   solo copia propiedades PROPIAS ENUMERABLES. `Error.prototype.name` (accessor
 *   del prototipo) y `error.message` (propia pero NO enumerable) desaparecían en
 *   el proceso; del error sólo sobrevivían las enumerables del motor (`stack`, y
 *   `digest` en los errores de Next.js). El `stringify()` siguiente serializaba
 *   ese esqueleto y `new Error(blob)` mandaba a Sentry exactamente esto:
 *
 *     Error: {"stack":"@…/0ge55w42-ozsm.js:15:1589\n@…:2:99698…"}
 *
 *   Sin nombre de clase, sin mensaje, y con el stack escondido en `extra` en vez
 *   de ser el stack de la excepción → 17 días sin poder triar. El `n` suelto del
 *   blob viene del propio `error.stack` del motor (llega idéntico en `extra.stack`,
 *   que no pasa por ningún saneador de saltos de línea), no de este archivo.
 *
 *   Fix: `readErrorDescription()` lee `name`/`message`/`stack`/`digest` por acceso
 *   a propiedad (funciona con o sin enumerabilidad: Error, DOMException, objetos
 *   cross-realm, payloads planos), `firstMeaningfulStackLine()`/`nameFromStack()`
 *   recuperan lo que el caller no pasó explícitamente, y la captura se hace con
 *   la EXCEPCIÓN REAL (`name` + `stack` originales) para que Sentry agrupe por el
 *   lugar del fallo y pueda source-mapearlo. La sanitización PII no cambia: todo
 *   lo que sale del navegador sigue pasando por `sanitizeString` igual que antes.
 *
 *   DEFECTO B — un error de navegador creaba DOS issues.
 *   Ver `logError` en src/lib/admin-events.ts (flags `sentry` / `sentryTraceId`).
 */
import { logger, sanitizeString, sanitizeValue } from "./logger";
import { stringify } from "./shared-utils";
import {
  isHydrationMismatchNoise,
  shouldSuppressClientErrorReport,
} from "./browser-console-noise";
// Sentry optional — dynamic import keeps client reporting fail-open.
// 2026-09-08 estabilidad: memoizado a nivel de módulo — una ráfaga de errores
// no dispara N chunk-loads del SDK.
// 2026-10-02: `getActiveSpan`/`getCurrentScope` para leer el trace_id con el que
// el navegador captura el evento — es la clave de deduplicación del relay.
type OptionalSentry = {
  captureException?: (exception: unknown, context?: unknown) => unknown;
  getActiveSpan?: () =>
    { spanContext?: () => { traceId?: string } } | undefined;
  getCurrentScope?: () =>
    | {
        getPropagationContext?: () => { traceId?: string } | undefined;
      }
    | undefined;
};

let sentryMod: OptionalSentry | null = null;
/**
 * 2026-10-11 SHA-256 síncrono puro (FIPS 180-4, sin deps, browser-safe).
 * `crypto.subtle.digest` es asíncrono y el check de dedup es síncrono, así que
 * se implementa aquí (~1KB, solo para claves de dedup, nunca para seguridad).
 */
const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function sha256Hex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  const bitLen = bytes.length * 8;
  const paddedLen = (((bytes.length + 8) >> 6) + 1) << 6;
  const padded = new Uint8Array(paddedLen);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLen - 4, bitLen >>> 0);
  view.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000));
  let h0 = 0x6a09e667,
    h1 = 0xbb67ae85,
    h2 = 0x3c6ef372,
    h3 = 0xa54ff53a;
  let h4 = 0x510e527f,
    h5 = 0x9b05688c,
    h6 = 0x1f83d9ab,
    h7 = 0x5be0cd19;
  const w = new Array<number>(64);
  for (let off = 0; off < paddedLen; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const a15 = w[i - 15]!;
      const a2 = w[i - 2]!;
      const a16 = w[i - 16]!;
      const a7 = w[i - 7]!;
      const s0 =
        ((a15 >>> 7) | (a15 << 25)) ^
        ((a15 >>> 18) | (a15 << 14)) ^
        (a15 >>> 3);
      const s1 =
        ((a2 >>> 17) | (a2 << 15)) ^ ((a2 >>> 19) | (a2 << 13)) ^ (a2 >>> 10);
      w[i] = (a16 + s0 + a7 + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = [h0, h1, h2, h3, h4, h5, h6, h7];
    for (let i = 0; i < 64; i++) {
      const s1 =
        ((e >>> 6) | (e << 26)) ^
        ((e >>> 11) | (e << 21)) ^
        ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + s1 + ch + SHA256_K[i]! + w[i]!) | 0;
      const s0 =
        ((a >>> 2) | (a << 30)) ^
        ((a >>> 13) | (a << 19)) ^
        ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((v) => (v >>> 0).toString(16).padStart(8, "0"))
    .join("");
}

// 2026-10-04 CPU-diet: dedup en memoria por mensaje+path (ventana 60s, cap
// 200 claves). Best-effort por tab; el rate 10/min/IP del servidor sigue
// siendo el control duro.
// 2026-10-11 dedup-hash (research 2026 GDPR art.17 data-minimisation: la clave
// anterior guardaba el MENSAJE CRUDO (PII potencial) como clave del Map en
// memoria del tab). Ahora: sha256(normalizado)|path — el normalizado es el
// mensaje ya sanitizado (PII redactada), en minúsculas y truncado a 200 chars
// (misma clase de error = misma clave aunque cambie un ID); el path va en
// claro para acotar la colisión entre rutas. Sin message crudo en memoria.
const CLIENT_ERROR_DEDUP_MS = 60_000;
const CLIENT_ERROR_DEDUP_KEY_LEN = 200;
const recentClientErrorMs = new Map<string, number>();

/** Clave de dedup: sha256(sanitizado+minúsculas+≤200)|path — sin PII cruda. */
export function clientErrorDedupKey(
  message: unknown,
  pathname?: string,
): string {
  const normalized = sanitizeString(stringify(message) || "")
    .toLowerCase()
    .slice(0, CLIENT_ERROR_DEDUP_KEY_LEN);
  return `${sha256Hex(normalized)}|${pathname ?? ""}`;
}

/** Test-only: resetea la ventana dedup entre tests. */
export function __resetClientErrorDedupForTests(): void {
  recentClientErrorMs.clear();
}
async function getSentry(): Promise<OptionalSentry> {
  if (sentryMod) return sentryMod;
  try {
    const mod = await import("@sentry/nextjs").catch(() => null);
    if (mod) sentryMod = (mod.default ?? mod) as unknown as OptionalSentry;
  } catch {}
  if (!sentryMod) sentryMod = { captureException: () => {} };
  return sentryMod;
}

// 2026-09-10 Sentry dev-gate: fuera de prod (dev) o sin DSN no se reporta nada// a Sentry ni a /api/error-report (0 eventos) — solo logger.warn. Prod intacto.
// Tests (NODE_ENV=test/VITEST) ejercen la ruta prod para no romper paridad.
function isClientReportingEnabled(): boolean {
  if (process.env.NODE_ENV === "test" || process.env.VITEST) return true;
  if (process.env.NODE_ENV !== "production") return false;
  try {
    return Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN);
  } catch {
    return false;
  }
}

interface ClientErrorReportInput {
  message: unknown;
  stack?: unknown;
  digest?: unknown;
  locationHref?: string;
  pathname?: string;
  userAgent?: string;
}

export function formatConsoleError(args: unknown[]): string {
  return args
    .map((arg) => stringify(arg))
    .filter(Boolean)
    .join(" ")
    .slice(0, 1000);
}

// 2026-09-01 PII filter — Sentry Gaps 2026: redact email, base64, token, password, secret before Sentry
// 2026-10-11 SSOT: la implementación vive en ./logger (sanitizeString/
// sanitizeValue exportados) — este archivo solo los importa. Antes duplicaba
// las regexes y divergía (sin PHONE_RE ni always-secret por keyHint): un fix
// de PII en uno no llegaba al otro. Ver src/lib/logger.ts.

// ─────────────────────────────────────────────────────────────────────────────
// 2026-10-02 Sentry JAVASCRIPT-NEXTJS-1S/1P — lectura del error REAL.
// Ver el bloque Defecto A de la cabecera del archivo: `Object.entries()` (y por
// tanto `sanitizeValue`) descarta `name`/`message` de un Error. Estas funciones
// acceden a las propiedades por nombre, así que funcionan con o sin
// enumerabilidad, en `Error`, `DOMException`, errores de otro realm (iframe /
// worker — donde `instanceof Error` es false) y en objetos planos `{stack}`.
// Deliberadamente NO vuelcan el objeto entero: sólo name/message/stack/digest,
// o sea menos datos que el `JSON.stringify` que había antes.

export interface ErrorDescription {
  /** Nombre de la clase (`TypeError`, `DOMException`, …). Sin enumerate. */
  name?: string;
  /** Mensaje legible. Sin enumerate. */
  message?: string;
  /** Stack original del motor (o el que la envoltura HTML había copiado). */
  stack?: string;
  /** Digest de Next.js — liga el error del cliente con el log del servidor. */
  digest?: string;
  /** `true` si `value` era un envelope JSON tipo `{"stack":"…"}`. */
  isEnvelope?: boolean;
}

/** Lee una propiedad string (propia o heredada) sin lanzar con getters exóticos. */
function readStringProp(value: object, key: string): string | undefined {
  try {
    const raw = (value as Record<string, unknown>)[key];
    if (typeof raw !== "string") return undefined;
    return raw.trim().length > 0 ? raw : undefined;
  } catch {
    return undefined;
  }
}

/** Primera línea de un stack con contenido real (V8, JSC y SpiderMonkey alike). */
export function firstMeaningfulStackLine(
  stack: string | undefined,
): string | undefined {
  if (!stack) return undefined;
  for (const line of stack.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length > 0 && !isEmptyLikeMessage(trimmed)) return trimmed;
  }
  return undefined;
}

/**
 * V8 formatea el stack como `TypeError: boom\n    at …`. Cuando el caller solo
 * pasó `error.message` (ErrorBoundary lo hace), el nombre de la clase sigue
 * siendo legible en esa primera línea y así lo recuperamos — sin él, Sentry
 * agruparía un TypeError como si fuera un Error genérico.
 */
const STACK_HEAD_NAME_RE =
  /^([A-Za-z_$][A-Za-z0-9_$.]*(?:Error|Exception)|Error|Warning)\b\s*:/;

function nameFromStack(stack: string | undefined): string | undefined {
  const head = firstMeaningfulStackLine(stack);
  if (!head) return undefined;
  const match = STACK_HEAD_NAME_RE.exec(head);
  return match?.[1];
}

/**
 * `url:línea:columna` del primer frame. Turbopack minifica los nombres de
 * función pero NO cambia ese formato, así que el punto de fallo sigue siendo
 * localizable aunque el stack llegue sin nombres.
 */
const STACK_FRAME_RE = /(?:https?:\/\/|\/)[^\s()]*:\d+:\d+/;

function firstStackSource(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  for (const line of stack.split("\n")) {
    const match = STACK_FRAME_RE.exec(line);
    if (match) return match[0].slice(0, 300);
  }
  return undefined;
}

/**
 * Desenvuelve un envelope JSON (`{"stack":"…"}`, `{"name":…,"message":…}`) para
 * que un chunk ya cacheado — o `formatConsoleError` sobre un objeto plano — no
 * vuelva a reportar un issue ilegible. Si no es un envelope con campos de error,
 * devuelve el texto tal cual (comportamiento previo intacto).
 */
function mergeEnvelope(desc: ErrorDescription, raw: string): void {
  const text = raw.trim();
  if (!text.startsWith("{")) return;
  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;
    const name = readStringProp(parsed as object, "name");
    const message = readStringProp(parsed as object, "message");
    const stack = readStringProp(parsed as object, "stack");
    const digest = readStringProp(parsed as object, "digest");
    // `{}` no aporta nada — no lo marques como envelope.
    if (!name && !message && !stack && !digest) return;
    desc.isEnvelope = true;
    desc.name ??= name;
    // El mensaje del envelope SUSTITUYE al blob: es el campo real. Si el
    // envelope no trae `message`, se vacía para que el llamador derive del
    // stack (era `{"stack":"…"}`, o sea 1S/1P entero).
    desc.message = message;
    desc.stack ??= stack;
    desc.digest ??= digest;
  } catch {
    // No era JSON — el texto original sigue siendo el mensaje.
  }
}

/**
 * Normaliza cualquier cosa que un caller pueda pasar como `message` a los campos
 * de error que Sentry y admin_events necesitan. Es la lectura equivalente en el
 * servidor (relay) y en el navegador, para que ambososinventen nombres distintos.
 */
export function readErrorDescription(value: unknown): ErrorDescription {
  if (typeof value === "string") {
    const desc: ErrorDescription = { message: value };
    mergeEnvelope(desc, value);
    return desc;
  }
  if (value === null || typeof value !== "object") return {};
  const desc: ErrorDescription = {};
  desc.name = readStringProp(value, "name");
  desc.message = readStringProp(value, "message");
  desc.stack = readStringProp(value, "stack");
  desc.digest = readStringProp(value, "digest");
  // Un objeto plano con `stack`/`digest` pero sin `message` (rejection de otro
  // realm, error serializado, `digest` de Next.js) tiene la forma que produjo
  // 1S/1P: serializarlo da `{"stack":"…"}` y se pierde todo. Se marca para que
  // el llamador NO vuelva a caer en el fallback JSON y use estos campos.
  desc.isEnvelope = Boolean(desc.name || desc.stack || desc.digest);
  return desc;
}

// Mensajes que no dicen nada (mismo criterio que isEmptyErrorMessage en
// admin-events.ts y que la rama `{}` de este archivo).
function isEmptyLikeMessage(value: string): boolean {
  return /^\s*(\{\}|\[\]|null|undefined|unknown( client)? error|\[object Object\])?\s*$/i.test(
    value ?? "",
  );
}

interface SanitizedClientReport {
  name?: string;
  message: string;
  stack?: string;
  digest?: string;
  pathname?: string;
  source?: string;
}

/**
 * Lee el error de verdad y le pasa `sanitizeString` (email/base64/JWT/Bearer +
 * tope de longitud) — exactamente la misma redacción de siempre, ahora
 * aplicada a los campos que antes se perdían.
 */
function buildSanitizedClientReport(
  input: ClientErrorReportInput,
): SanitizedClientReport {
  const desc = readErrorDescription(input.message);
  const stackRaw =
    desc.stack ?? (typeof input.stack === "string" ? input.stack : undefined);
  const digestRaw =
    desc.digest ??
    (typeof input.digest === "string" ? input.digest : undefined);
  // Sin name/message legibles (payload plano, p.ej. `console.error({code:"E"})`)
  // se conserva la serialización JSON de siempre: es todo lo que hay. Un envelope
  // ya desenvuelto NO debe volver a caer aquí (si no, reintroduciríamos el blob).
  const rawMessage =
    desc.message ??
    (desc.isEnvelope
      ? undefined
      : sanitizeString(stringify(sanitizeValue(input.message)) || ""));
  const stack = stackRaw ? sanitizeString(stackRaw) : undefined;
  const name = desc.name
    ? sanitizeString(desc.name).slice(0, 200)
    : nameFromStack(stack);
  return {
    name,
    message: sanitizeString(rawMessage ?? "").slice(0, 1000),
    stack,
    digest: digestRaw ? sanitizeString(digestRaw).slice(0, 200) : undefined,
    pathname: input.pathname ? sanitizeString(input.pathname) : undefined,
    source: firstStackSource(stack),
  };
}

/**
 * Sentry imprime `name: message`, así que el mensaje NO debe repetir el nombre
 * de clase (`TypeError: TypeError: …`). Además, cuando el mensaje viene de la
 * cabecera del stack (`TypeError: x is not a function`) el prefijo se quita y
 * queda como `name`, que es donde Sentry agrupa por tipo.
 */
function stripNamePrefix(message: string, name: string | undefined): string {
  if (!name) return message;
  const prefix = `${name}:`;
  return message.toLowerCase().startsWith(prefix.toLowerCase())
    ? message.slice(prefix.length).trimStart()
    : message;
}

/**
 * Mensaje final para Sentry: el mensaje real sin prefijo de clase (éste va en
 * `name`), o la primera línea real del stack, o el digest de Next.js (que sí
 * liga con el log del servidor). `null` = descartar: un issue sin mensaje es
 * exactamente el ruido que se pagó en 1S/1P.
 */
function resolveEffectiveMessage(report: SanitizedClientReport): string | null {
  if (report.message && !isEmptyLikeMessage(report.message)) {
    return sanitizeString(stripNamePrefix(report.message, report.name)).slice(
      0,
      500,
    );
  }
  const stackHead = firstMeaningfulStackLine(report.stack);
  if (stackHead) {
    return sanitizeString(stripNamePrefix(stackHead, report.name)).slice(
      0,
      500,
    );
  }
  if (report.digest) {
    // Error de Server Component: Next.js registra el digest en el log del
    // servidor, así que sigue siendo accionable (a diferencia de `{}` pelado).
    return `Unhandled server error (digest ${report.digest})`.slice(0, 200);
  }
  return null;
}

/** trace_id con el que el navegador está reportando — clave de dedup del relay. */
function readSentryTraceId(Sentry: OptionalSentry): string | undefined {
  try {
    const span =
      typeof Sentry.getActiveSpan === "function"
        ? Sentry.getActiveSpan()
        : undefined;
    const spanTrace = span?.spanContext?.()?.traceId;
    if (spanTrace) return String(spanTrace);
    const scope =
      typeof Sentry.getCurrentScope === "function"
        ? Sentry.getCurrentScope()
        : undefined;
    const scopeTrace = scope?.getPropagationContext?.()?.traceId;
    return scopeTrace ? String(scopeTrace) : undefined;
  } catch {
    return undefined;
  }
}

function buildClientErrorReport(
  input: ClientErrorReportInput,
  report: SanitizedClientReport,
  message: string,
  traceId?: string,
) {
  return {
    // `message` va SIN el prefijo de clase y `name` APARTE: el relay compone
    // "Nombre: mensaje" al persistir, sin tener que parsear una cadena.
    message,
    name: report.name,
    source: "client" as const,
    stack: report.stack,
    digest: report.digest,
    // 2026-10-02 Defecto B: el navegador ya capturó este error en Sentry con
    // este trace — el relay lo usa para NO crear un segundo issue.
    traceId,
    url: input.locationHref ? sanitizeString(input.locationHref) : undefined,
    path: report.pathname,
    userAgent: input.userAgent,
  };
}

// 2026-09-06 Sentry JAVASCRIPT-NEXTJS-6: los errores transitorios del stream
// (timeout, red, saturación del modelo) ya los loguea el servidor con
// diagnóstico completo. Reportarlos también desde el cliente duplicaba cada
// fallo en 2 issues (6=cliente, 7=servidor vía /api/error-report). Se omiten
// en cliente (solo console.warn) — el servidor es la única fuente.
const TRANSIENT_CLIENT_RE =
  /tardó demasiado|took too long|timed?\s*out|deadline.*exceeded|failed to fetch|fetch failed|networkerror|chat stream stalled|chat stream empty|sendmessage:stream|overloaded|service unavailable|temporarily unavailable|\b503\b|resource.*exhausted|quota.*exceeded|too many requests|rate.?limit|\b429\b|saturad|at capacity|high demand/i;

export function isTransientClientError(message: string): boolean {
  return TRANSIENT_CLIENT_RE.test(message ?? "");
}

// 2026-09-06 Sentry JAVASCRIPT-NEXTJS-6/7: "[chat:handleFile] Unsupported file
// type" es validación UX esperada (zip/exe/no-soportado), no bug. Nunca va a
// Sentry ni a /api/error-report — handleFile ya muestra toast i18n.
const EXPECTED_FILE_ERROR_RE =
  /unsupported file type|chatfileunsupported|file is too large|chatfiletoolarge|file contains no readable text|chatfileempty|file is malformed|chatfilemalformed|looks scanned|image-only|no extractable text|audio files go through the audio attachment|\[chat:handlefile\]/i;

export function isExpectedFileError(message: string): boolean {
  return EXPECTED_FILE_ERROR_RE.test(message ?? "");
}

export async function reportClientError(
  input: ClientErrorReportInput,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  // 2026-09-10 dev-gate: 0 eventos Sentry en dev o sin DSN (solo logger.warn).
  if (!isClientReportingEnabled()) {
    logger.warn("[client-error] suppressed (dev/no-DSN, no Sentry)");
    return;
  }
  // 2026-10-04 CPU-diet: un error en render con reintento, o un console.error
  // en un poller roto, generaba 1 Sentry envelope + 1 POST /api/error-report
  // por ocurrencia (hasta 10 POSTs/min/IP). Misma clase+path dentro de 60s
  // = 1 envío; el resto solo warn local. No cambia UX (telemetría fail-open).
  // 2026-10-11: la clave es hash (sin message crudo en memoria).
  const dedupKey = clientErrorDedupKey(input.message, input.pathname);
  const nowMs = Date.now();
  const lastMs = recentClientErrorMs.get(dedupKey) ?? 0;
  if (nowMs - lastMs < CLIENT_ERROR_DEDUP_MS) {
    logger.warn("[client-error] suppressed (duplicate within 60s, no Sentry)");
    return;
  }
  recentClientErrorMs.set(dedupKey, nowMs);
  if (recentClientErrorMs.size > 200) {
    const oldest = [...recentClientErrorMs.entries()].sort(
      (a, b) => a[1] - b[1],
    )[0]?.[0];
    if (oldest) recentClientErrorMs.delete(oldest);
  }
  // 2026-10-02 Defecto A: se lee el error REAL (name/message/stack/digest) en
  // vez de serializar su esqueleto a JSON. Misma redacción PII, más campos.
  const report = buildSanitizedClientReport(input);
  const messageStr = report.message || "Unknown client error";

  // 2026-09-06 Sentry JAVASCRIPT-NEXTJS-6/7: transitorios no van a Sentry ni a
  // /api/error-report (el servidor ya los tiene en admin_events). Solo warn.
  // 2026-09-06 JAVASCRIPT-NEXTJS-6/7 (archivos): "Unsupported file type" es
  // validación UX esperada, nunca Sentry ni POST — handleFile ya da toast.
  if (isTransientClientError(messageStr) || isExpectedFileError(messageStr)) {
    logger.warn("[client-error] suppressed (expected/transient, no Sentry)", {
      message: messageStr.slice(0, 200),
    });
    return;
  }

  // 2026-09-08 Sentry JAVASCRIPT-NEXTJS-K/6: hydration mismatch (#418, `{}`).
  // 2026-09-08 resto del ruido benigno (bots, chunk-load, extensiones, CSP):
  // se filtra aquí también para no pagar Sentry captureException antes del
  // gate del servidor.
  const gateText = [messageStr, report.stack ?? ""].join("\n");
  if (
    isHydrationMismatchNoise(gateText) ||
    shouldSuppressClientErrorReport(gateText, input.userAgent)
  ) {
    logger.warn(
      "[client-error] suppressed (hydration/benign noise, no Sentry)",
      {
        path: report.pathname,
      },
    );
    return;
  }

  // 2026-09-07 Sentry JAVASCRIPT-NEXTJS-E: motivo `{}` / envelope JSON vacío
  // (rejection con objeto plano) creaba issues inútiles `Error: {}` — derivar
  // del stack o del digest, o descartar antes de reportar.
  const effectiveMessage = resolveEffectiveMessage(report);
  if (!effectiveMessage) {
    logger.warn("[client-error] suppressed (empty message, no stack)", {
      path: report.pathname,
    });
    return;
  }

  // 2026-10-02 Defecto B (dedup por trace): el trace_id con el que este evento
  // entra en Sentry viaja en el POST del relay. El servidor lo usa como clave
  // para NO volver a capturar — un error del navegador = un issue, no dos.
  let traceId: string | undefined;
  // Sentry único — captureException (external webhook removed 2026-09-01, Sentry Gaps 2026) — dynamic so build does not fail
  try {
    const Sentry = await getSentry();
    traceId = readSentryTraceId(Sentry);
    if (typeof Sentry.captureException === "function") {
      // Excepción REAL: `new Error(<blob JSON>)` (1S/1P) perdía `name`, no
      // agrupaba por tipo y dejaba el stack en `extra`. Ahora el objeto
      // capturado lleva el nombre de clase y el stack originales, así que Sentry
      // agrupa por el lugar del fallo y puede source-mapearlo.
      const sentryError = new Error(effectiveMessage);
      if (report.name) sentryError.name = report.name;
      if (report.stack) sentryError.stack = report.stack;
      Sentry.captureException(sentryError, {
        tags: {
          source: "client",
          ...(traceId ? { sentryTraceId: traceId } : {}),
        },
        extra: {
          pathname: report.pathname,
          errorName: report.name,
          // url:línea:columna del primer frame — legible aunque Turbopack haya
          // minificado los nombres de función.
          errorSource: report.source,
          digest: report.digest ? "[redacted]" : undefined,
        },
        level: "error",
      });
    }
  } catch {
    // Sentry fail-open — never throw from client
  }

  try {
    await fetcher("/api/error-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        buildClientErrorReport(input, report, effectiveMessage, traceId),
      ),
      keepalive: true,
    });
  } catch (err) {
    logger.error("Failed to send error report", { error: String(err) });
    return;
  }
}
